'use client';

import { useState } from 'react';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';
import { useParams } from 'next/navigation';

import { Button } from '@/components/ui/button/button.component';
import { ModalCloseButton } from '@/components/ui/modal-close-button/modal-close-button.component';
import { Sheet, SheetClose, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';

type DiscardChangesModalProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  onConfirm: (value: boolean) => Promise<void>;
};

export const DiscardChangesModal = ({ open, setOpen, onConfirm }: DiscardChangesModalProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const analytics = getAnalyticsPlugin();
  const params = useParams();
  const dependentId = params?.id as string | undefined;

  const handleSaveChanges = async (): Promise<void> => {
    if (dependentId) {
      analytics.track(AnalyticsEnum.DEPENDENTS_EDIT_KEEP_EDITING, {
        dependent_id: dependentId,
      });
    } else {
      analytics.track(AnalyticsEnum.DEPENDENTS_NEW_KEEP_EDITING);
    }

    setIsLoading(true);
    try {
      await onConfirm(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDiscardChanges = (): void => {
    if (dependentId) {
      analytics.track(AnalyticsEnum.DEPENDENTS_EDIT_DISCARD_CHANGES, {
        dependent_id: dependentId,
      });
    } else {
      analytics.track(AnalyticsEnum.DEPENDENTS_NEW_DISCARD_CHANGES);
    }
    onConfirm(true);
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        data-testid={DataTestIdEnum.DEPENDENTS_DISCARD_CHANGES_MODAL}
        side="centerFit"
        hasHeader
        className="p-0 md:bg-transparent border-none overflow-x-hidden"
      >
        <SheetHeader className="bg-white">
          <div className="w-full flex justify-between items-center p-6 px-5 pb-6">
            <SheetTitle>
              <Typography
                data-testid={DataTestIdEnum.DEPENDENTS_DISCARD_CHANGES_MODAL_TITLE}
                variant={TypographyVariant.MODAL_TITLE}
              >
                Descartar alterações?
              </Typography>
            </SheetTitle>
            <SheetClose asChild>
              <ModalCloseButton
                className="self-center"
                disabled={isLoading}
              />
            </SheetClose>
          </div>
        </SheetHeader>

        <div className="p-5 pt-0 bg-white flex flex-col gap-6">
          <Typography className="text-wrap">
            Você fez alterações que ainda não foram salvas. Se continuar, essas mudanças serão perdidas.
          </Typography>

          <div className="flex flex-col gap-4">
            <Button
              data-testid={DataTestIdEnum.DEPENDENTS_DISCARD_CHANGES_MODAL_SAVE_BUTTON}
              isLoading={isLoading}
              onClick={handleSaveChanges}
            >
              Salvar alterações
            </Button>

            <Button
              data-testid={DataTestIdEnum.DEPENDENTS_DISCARD_CHANGES_MODAL_DISCARD_BUTTON}
              variant="secondary"
              disabled={isLoading}
              onClick={handleDiscardChanges}
            >
              Continuar sem salvar
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
